import { useState, useEffect } from 'react';
import { SidebarProvider } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/AppSidebar';
import NavBar from '@/components/NavBar';
import { ArrowLeft, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import EmailSequenceForm from '@/components/email-sequence/EmailSequenceForm';
import EmailSequencePreview from '@/components/email-sequence/EmailSequencePreview';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export interface EmailSequenceData {
  id?: string;
  prospectName: string;
  companyName: string;
  websiteUrl?: string;
  linkedinUrl?: string;
  industry?: string;
  companySize?: string;
  leadSource?: string;
  painPoint?: string;
  emailTone?: string;
  numEmails: number;
  timeGapDays: number;
  productFocus?: string;
  researchData?: any;
  emailSequence?: any[];
  createdAt?: string;
}

const EmailSequence = () => {
  const [formData, setFormData] = useState<EmailSequenceData | null>(null);
  const [generatedSequence, setGeneratedSequence] = useState<any>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [savedSequences, setSavedSequences] = useState<EmailSequenceData[]>([]);
  const [selectedSequence, setSelectedSequence] = useState<EmailSequenceData | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  useEffect(() => {
    loadSavedSequences();
  }, []);

  const loadSavedSequences = async () => {
    try {
      const { data, error } = await supabase
        .from('email_sequences')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      if (data) {
        setSavedSequences(data.map(seq => ({
          id: seq.id,
          prospectName: seq.prospect_name,
          companyName: seq.company_name,
          websiteUrl: seq.website_url,
          linkedinUrl: seq.linkedin_url,
          industry: seq.industry,
          companySize: seq.company_size,
          leadSource: seq.lead_source,
          painPoint: seq.pain_point,
          emailTone: seq.email_tone,
          numEmails: seq.num_emails,
          timeGapDays: seq.time_gap_days,
          productFocus: seq.product_focus,
          researchData: seq.research_data,
          emailSequence: Array.isArray(seq.email_sequence) ? seq.email_sequence : [],
          createdAt: seq.created_at
        })));
      }
    } catch (error) {
      console.error('Error loading saved sequences:', error);
      toast.error('Failed to load saved sequences');
    }
  };

  const handleFormSubmit = async (data: EmailSequenceData) => {
    setFormData(data);
    setIsGenerating(true);
    
    // Show loading toast
    const loadingToast = toast.loading('Generating email sequence with AI research... This may take 30-60 seconds');
    
    try {
      const { data: result, error } = await supabase.functions.invoke('generate-email-sequence', {
        body: data
      });

      if (error) {
        toast.dismiss(loadingToast);
        const errorMessage = error.message || 'Failed to generate email sequence';
        toast.error(errorMessage);
        throw error;
      }

      toast.dismiss(loadingToast);
      toast.success('Email sequence generated successfully!');
      setGeneratedSequence(result);
      setShowPreview(true);
    } catch (error: any) {
      toast.dismiss(loadingToast);
      console.error('Error generating email sequence:', error);
      
      // Extract more specific error information
      const errorMessage = error?.message || 
                          error?.error?.message || 
                          'Failed to generate email sequence. Please try again.';
      
      toast.error(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveSequence = async () => {
    if (!formData || !generatedSequence) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to save sequences');
        return;
      }

      const { error } = await supabase
        .from('email_sequences')
        .insert({
          user_id: user.id,
          prospect_name: formData.prospectName,
          company_name: formData.companyName,
          website_url: formData.websiteUrl,
          linkedin_url: formData.linkedinUrl,
          industry: formData.industry,
          company_size: formData.companySize,
          lead_source: formData.leadSource,
          pain_point: formData.painPoint,
          email_tone: formData.emailTone,
          num_emails: formData.numEmails,
          time_gap_days: formData.timeGapDays,
          product_focus: formData.productFocus,
          research_data: generatedSequence.research,
          email_sequence: generatedSequence.emails
        });

      if (error) throw error;

      toast.success('Email sequence saved successfully');
      loadSavedSequences();
      setShowPreview(false);
      setGeneratedSequence(null);
      setFormData(null);
    } catch (error) {
      console.error('Error saving sequence:', error);
      toast.error('Failed to save sequence');
    }
  };

  const viewSequence = (sequence: EmailSequenceData) => {
    setSelectedSequence(sequence);
    setShowPreview(true);
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        <div className="flex-1 relative bg-gray-50">
          <NavBar onSignOut={() => {}} isAuthenticated={isAuthenticated} />
          <main className="p-8 pt-24">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-4 mb-8">
                {isMobile && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate(-1)}
                    className="shrink-0"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                )}
                <div>
                  <h1 className="text-3xl font-bold">Email Sequence Generator</h1>
                  <p className="text-gray-600 mt-1">
                    Create personalized email sequences with deep prospect research
                  </p>
                </div>
              </div>

              <div className="space-y-8">
                {/* Form Section */}
                <Card>
                  <CardHeader>
                    <CardTitle>Generate New Email Sequence</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <EmailSequenceForm 
                      onSubmit={handleFormSubmit}
                      isGenerating={isGenerating}
                    />
                  </CardContent>
                </Card>

                {/* Results Table */}
                {savedSequences.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Previous Email Sequences</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Prospect</TableHead>
                            <TableHead>Company</TableHead>
                            <TableHead>Emails</TableHead>
                            <TableHead>Created</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {savedSequences.map((sequence) => (
                            <TableRow key={sequence.id}>
                              <TableCell className="font-medium">
                                {sequence.prospectName}
                              </TableCell>
                              <TableCell>{sequence.companyName}</TableCell>
                              <TableCell>{sequence.numEmails} emails</TableCell>
                              <TableCell>
                                {sequence.createdAt 
                                  ? new Date(sequence.createdAt).toLocaleDateString()
                                  : 'N/A'
                                }
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => viewSequence(sequence)}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Preview Dialog */}
      {showPreview && (generatedSequence || selectedSequence) && (
        <EmailSequencePreview
          sequence={generatedSequence || { 
            research: selectedSequence?.researchData,
            emails: selectedSequence?.emailSequence 
          }}
          formData={formData || selectedSequence}
          isOpen={showPreview}
          onClose={() => {
            setShowPreview(false);
            setSelectedSequence(null);
            if (!selectedSequence) {
              setGeneratedSequence(null);
              setFormData(null);
            }
          }}
          onSave={generatedSequence ? handleSaveSequence : undefined}
        />
      )}
    </SidebarProvider>
  );
};

export default EmailSequence;